import type { TradingPair } from '../../types/pair';
import type { OHLCV, Timeframe } from '../../types/ohlcv';
import type { Ticker24h } from '../../types/ticker';
import { ProviderError } from '../types';
import { aggregateToTimeframe, bucketStart } from '../../utils/aggregate';
import { ReconnectingSocket } from './ws';
import { FEW_TICKERS } from './types';
import type { AdapterOptions, ExchangeAdapter } from './types';

/**
 * Kraken spot adapter (public endpoints, no key; REST answers CORS by echoing
 * the request Origin, and has no preflight — keep requests simple GETs).
 *
 * Verified live 2026-10-02:
 *   - GET /0/public/OHLC?pair=P&interval=MIN&since=S → `{ error: [], result:
 *     { <pair key>: rows, last } }`. The result key can differ from the request
 *     (XBTUSD → XXBTZUSD). Rows are `[time s, open, high, low, close, vwap,
 *     volume, count]` (prices as strings), oldest first, one per interval even
 *     without trades, the last one still forming. HISTORY LIMIT: only the newest
 *     720 closed candles (plus the forming one) are ever returned; `since` trims
 *     that set but cannot reach further back, so older windows resolve [].
 *   - Intervals 1 5 15 30 60 240 1440 10080 21600 minutes. 4h and 1D open on
 *     UTC boundaries, but 10080 candles open on Thursdays and 21600 is 15 days,
 *     so weeks and months are rolled up from days here.
 *   - GET /0/public/AssetPairs: `wsname` ("XBT/USD"), status online /
 *     cancel_only / post_only, pair_decimals, lot_decimals.
 *   - GET /0/public/Ticker without `pair` returns every market (plus keys
 *     AssetPairs does not list, e.g. "AAVEUSD:BTNL"): c[0] last, o = today's
 *     open at 00:00 UTC, v[1] / p[1] 24h volume / vwap, h[1] / l[1] 24h range.
 *   - wss://ws.kraken.com/v2 symbols are the wsnames with XBT → BTC and
 *     XDG → DOGE (identical for all 1456 pairs). `ohlc` sends a snapshot of the
 *     last 10 candles, then updates; `interval_begin` is the candle open. Only
 *     one ohlc interval per symbol per connection. `ticker` has a rolling 24h
 *     `change_pct`. A `{"channel":"heartbeat"}` frame arrives every second.
 */

const REST_URL = 'https://api.kraken.com/0/public';
const WS_URL = 'wss://ws.kraken.com/v2';
/** Kraken's OHLC history depth, in closed candles of any interval. */
const MAX_ROWS = 720;
const MARKETS_TTL_MS = 10 * 60_000;
const PING = { intervalMs: 30_000, message: '{"method":"ping"}' };

/** Native intervals, minutes. */
const INTERVAL_MINUTES: Partial<Record<Timeframe, number>> = {
  '1m': 1,
  '5m': 5,
  '15m': 15,
  '30m': 30,
  '1h': 60,
  '4h': 240,
  '1D': 1440,
};

/** Timeframes Kraken lacks (or misaligns), and the native one they are rolled up from. */
const ROLLUP_SOURCE: Partial<Record<Timeframe, Timeframe>> = {
  '3m': '1m',
  '2h': '1h',
  '6h': '1h',
  '12h': '4h',
  '3D': '1D',
  '1W': '1D',
  '1M': '1D',
};

/** The native timeframe (and its interval in minutes) fetched to serve `timeframe`. */
export function krakenInterval(timeframe: Timeframe): { source: Timeframe; minutes: number } {
  const source = ROLLUP_SOURCE[timeframe] ?? timeframe;
  const minutes = INTERVAL_MINUTES[source];
  if (!minutes) throw new ProviderError(`Unsupported timeframe ${timeframe}`);
  return { source, minutes };
}

const ASSET_ALIASES: Record<string, string> = { XBT: 'BTC', XDG: 'DOGE' };

/** Kraken's display asset code → the common one (XBT → BTC, XDG → DOGE). */
export function normaliseKrakenAsset(code: string): string {
  return ASSET_ALIASES[code] ?? code;
}

/** AssetPairs `wsname` ("XBT/USD") → canonical pair ("BTC/USD"); per asset, so "AIXBT/EUR" is left alone. */
export function krakenWsNameToPair(wsname: string): string {
  return wsname.split('/').map(normaliseKrakenAsset).join('/');
}

export interface KrakenAssetPair {
  altname: string;
  wsname?: string;
  status?: string;
  pair_decimals: number;
  lot_decimals: number;
}

export interface KrakenMarkets {
  /** Online markets, sorted by symbol. */
  pairs: TradingPair[];
  /** Canonical pair → AssetPairs key (accepted by every REST endpoint), for every status. */
  restNames: Map<string, string>;
  /** AssetPairs key → canonical pair, online markets only. */
  symbols: Map<string, string>;
}

export function indexKrakenAssetPairs(result: Record<string, KrakenAssetPair>): KrakenMarkets {
  const pairs: TradingPair[] = [];
  const restNames = new Map<string, string>();
  const symbols = new Map<string, string>();
  for (const [key, info] of Object.entries(result)) {
    if (!info?.wsname) continue;
    const symbol = krakenWsNameToPair(info.wsname);
    restNames.set(symbol, key);
    if (info.status !== 'online') continue;
    const [base, quote] = symbol.split('/');
    symbols.set(key, symbol);
    pairs.push({
      symbol,
      base,
      quote,
      exchangeId: 'kraken',
      priceDecimals: info.pair_decimals,
      quantityDecimals: info.lot_decimals,
    });
  }
  pairs.sort((a, b) => a.symbol.localeCompare(b.symbol));
  return { pairs, restNames, symbols };
}

/** OHLC `result` → oldest-first candles. Its pair key need not match the requested name. */
export function mapKrakenOHLC(result: Record<string, unknown>): OHLCV[] {
  const key = Object.keys(result).find((k) => k !== 'last');
  const rows = key === undefined ? undefined : result[key];
  if (!Array.isArray(rows)) throw new ProviderError('Unrecognized Kraken OHLC response');
  const out: OHLCV[] = [];
  for (const row of rows as unknown[]) {
    if (!Array.isArray(row) || row.length < 7) continue;
    const time = toNumber(row[0]);
    const open = toNumber(row[1]);
    const high = toNumber(row[2]);
    const low = toNumber(row[3]);
    const close = toNumber(row[4]);
    const volume = toNumber(row[6]);
    if (time === undefined || open === undefined || high === undefined || low === undefined) continue;
    if (close === undefined || volume === undefined) continue;
    out.push({ timestamp: time * 1000, open, high, low, close, volume });
  }
  out.sort((a, b) => a.timestamp - b.timestamp);
  return out;
}

export interface KrakenTicker {
  a?: string[];
  b?: string[];
  c?: string[];
  v?: string[];
  p?: string[];
  h?: string[];
  l?: string[];
  o?: string;
}

/**
 * `/Ticker` → tickers for the markets in `symbols` (AssetPairs key → pair).
 * REST `o` is today's 00:00 UTC open, not the price 24h ago, so here open24h
 * and changePercent24h describe the move since 00:00 UTC; the stream's
 * `change_pct` (parseKrakenTickerFrame) is a rolling 24h change.
 */
export function mapKrakenTickers(
  result: Record<string, KrakenTicker>,
  symbols: Map<string, string>,
  timestamp: number,
): Ticker24h[] {
  const tickers: Ticker24h[] = [];
  for (const [key, ticker] of Object.entries(result)) {
    const pair = symbols.get(key);
    const last = toNumber(ticker?.c?.[0]);
    if (!pair || last === undefined || last <= 0) continue;
    const open = toNumber(ticker.o);
    const volume = toNumber(ticker.v?.[1]);
    const vwap = toNumber(ticker.p?.[1]);
    tickers.push({
      exchangeId: 'kraken',
      pair,
      last,
      open24h: open,
      high24h: toNumber(ticker.h?.[1]),
      low24h: toNumber(ticker.l?.[1]),
      changePercent24h: open ? ((last - open) / open) * 100 : 0,
      baseVolume24h: volume,
      // volume × vwap is the exact quote volume; Kraken publishes no quote volume itself.
      quoteVolume24h: volume === undefined ? undefined : volume * (vwap ?? last),
      bid: toNumber(ticker.b?.[0]),
      ask: toNumber(ticker.a?.[0]),
      timestamp,
    });
  }
  return tickers;
}

interface WsFrame {
  channel?: string;
  type?: string;
  data?: unknown;
}

interface WsCandle {
  symbol?: string;
  interval?: number;
  interval_begin?: string;
  open?: number;
  high?: number;
  low?: number;
  close?: number;
  volume?: number;
}

/** A v2 `ohlc` frame for `symbol` at `minutes` → its candles, oldest first; null for any other frame. */
export function parseKrakenOhlcFrame(
  raw: string,
  symbol: string,
  minutes: number,
): { type: 'snapshot' | 'update'; candles: OHLCV[] } | null {
  const frame = parseFrame(raw);
  if (!frame || frame.channel !== 'ohlc' || !Array.isArray(frame.data)) return null;
  if (frame.type !== 'snapshot' && frame.type !== 'update') return null;
  const candles: OHLCV[] = [];
  for (const item of frame.data as WsCandle[]) {
    if (!item || item.symbol !== symbol || item.interval !== minutes) continue;
    const timestamp = parseTime(item.interval_begin);
    const open = toNumber(item.open);
    const high = toNumber(item.high);
    const low = toNumber(item.low);
    const close = toNumber(item.close);
    const volume = toNumber(item.volume);
    if (timestamp === undefined || open === undefined || high === undefined || low === undefined) continue;
    if (close === undefined || volume === undefined) continue;
    candles.push({ timestamp, open, high, low, close, volume });
  }
  candles.sort((a, b) => a.timestamp - b.timestamp);
  return { type: frame.type, candles };
}

interface WsTicker {
  symbol?: string;
  last?: number;
  bid?: number;
  ask?: number;
  high?: number;
  low?: number;
  volume?: number;
  vwap?: number;
  change?: number;
  change_pct?: number;
  timestamp?: string;
}

/** A v2 `ticker` snapshot/update frame → tickers; [] for any other frame. */
export function parseKrakenTickerFrame(raw: string): Ticker24h[] {
  const frame = parseFrame(raw);
  if (!frame || frame.channel !== 'ticker' || !Array.isArray(frame.data)) return [];
  if (frame.type !== 'snapshot' && frame.type !== 'update') return [];
  const tickers: Ticker24h[] = [];
  for (const item of frame.data as WsTicker[]) {
    const last = toNumber(item?.last);
    if (!item || typeof item.symbol !== 'string' || last === undefined || last <= 0) continue;
    const change = toNumber(item.change);
    const volume = toNumber(item.volume);
    const vwap = toNumber(item.vwap);
    tickers.push({
      exchangeId: 'kraken',
      pair: item.symbol,
      last,
      open24h: change === undefined ? undefined : last - change,
      high24h: toNumber(item.high),
      low24h: toNumber(item.low),
      changePercent24h: toNumber(item.change_pct) ?? 0,
      baseVolume24h: volume,
      quoteVolume24h: volume === undefined ? undefined : volume * (vwap ?? last),
      bid: toNumber(item.bid),
      ask: toNumber(item.ask),
      timestamp: parseTime(item.timestamp) ?? Date.now(),
    });
  }
  return tickers;
}

/**
 * The forming candle of `timeframe`, rolled up from finer candles that are
 * each re-sent as they grow (a native timeframe is a roll-up of one). REST
 * seeds fill the part of the bucket that was not streamed; streamed candles
 * always win over seeded ones. Only the newest bucket is kept.
 */
export class CandleRollup {
  private bucket = -Infinity;
  private readonly parts = new Map<number, OHLCV>();

  constructor(private readonly timeframe: Timeframe) {}

  /** A streamed finer candle; null when it belongs to a bucket older than the one being built. */
  update(candle: OHLCV): OHLCV | null {
    if (!this.accept(candle)) return null;
    this.parts.set(candle.timestamp, candle);
    return this.current();
  }

  /** REST finer candles; they never replace a streamed one. */
  seed(candles: OHLCV[]): OHLCV | null {
    for (const candle of candles) {
      if (this.accept(candle) && !this.parts.has(candle.timestamp)) this.parts.set(candle.timestamp, candle);
    }
    return this.current();
  }

  current(): OHLCV | null {
    if (this.parts.size === 0) return null;
    return aggregateToTimeframe([...this.parts.values()], this.timeframe)[0];
  }

  reset(): void {
    this.bucket = -Infinity;
    this.parts.clear();
  }

  private accept(candle: OHLCV): boolean {
    const bucket = bucketStart(candle.timestamp, this.timeframe);
    if (bucket < this.bucket) return false;
    if (bucket > this.bucket) {
      this.bucket = bucket;
      this.parts.clear();
    }
    return true;
  }
}

export function krakenAdapter(options: AdapterOptions = {}): ExchangeAdapter {
  let marketsCache: { at: number; markets: Promise<KrakenMarkets> } | null = null;

  async function getResult<T>(path: string): Promise<T> {
    const doFetch = options.fetch ?? globalThis.fetch;
    const response = await doFetch(`${REST_URL}${path}`, { headers: { Accept: 'application/json' } });
    const body = (await response.json().catch(() => null)) as { error?: unknown; result?: T } | null;
    const rawErrors = body?.error;
    const errors = Array.isArray(rawErrors) ? rawErrors.filter((e): e is string => typeof e === 'string') : [];
    if (!response.ok || errors.length > 0 || !body || body.result === undefined) {
      // Kraken reports most failures (unknown pair, rate limit) as HTTP 200 with `error` filled in.
      const message = errors.length > 0 ? errors.join('; ') : `Kraken request failed (${response.status})`;
      throw new ProviderError(message, response.ok ? undefined : response.status, body);
    }
    return body.result;
  }

  function loadMarkets(): Promise<KrakenMarkets> {
    if (marketsCache && Date.now() - marketsCache.at < MARKETS_TTL_MS) return marketsCache.markets;
    const markets = getResult<Record<string, KrakenAssetPair>>('/AssetPairs').then(indexKrakenAssetPairs);
    const entry = { at: Date.now(), markets };
    marketsCache = entry;
    // A failed load must not be cached.
    markets.catch(() => {
      if (marketsCache === entry) marketsCache = null;
    });
    return markets;
  }

  async function restNameFor(pair: string): Promise<string> {
    const name = (await loadMarkets()).restNames.get(pair.trim().toUpperCase());
    if (!name) throw new ProviderError(`Kraken has no market ${pair}`);
    return name;
  }

  /** Candles from `since` (Unix ms) on, and whether the 720-candle history cap cut them short. */
  async function fetchOHLC(restName: string, minutes: number, since: number): Promise<{ candles: OHLCV[]; truncated: boolean }> {
    const query = new URLSearchParams({
      pair: restName,
      interval: String(minutes),
      since: String(Math.max(0, Math.floor(since / 1000) - 1)),
    });
    const candles = mapKrakenOHLC(await getResult<Record<string, unknown>>(`/OHLC?${query}`));
    return { candles, truncated: candles.length >= MAX_ROWS && candles[0].timestamp > since };
  }

  const adapter: ExchangeAdapter = {
    id: 'kraken',
    name: 'Kraken',

    async getPairs(): Promise<TradingPair[]> {
      return (await loadMarkets()).pairs;
    },

    async getOHLCV({ pair, timeframe, from, to }): Promise<OHLCV[]> {
      const restName = await restNameFor(pair);
      const { source, minutes } = krakenInterval(timeframe);
      const inWindow = (candle: OHLCV) => candle.timestamp >= from && candle.timestamp <= to;
      if (source === timeframe) return (await fetchOHLC(restName, minutes, from)).candles.filter(inWindow);
      // Start on a bucket boundary so the first rolled-up candle is complete; the 720 cap bounds the fetch.
      const { candles, truncated } = await fetchOHLC(restName, minutes, bucketStart(from, timeframe));
      // When the cap starts the finer candles mid-bucket, that bucket is incomplete and dropped.
      const firstComplete = truncated ? candles[0].timestamp : -Infinity;
      return aggregateToTimeframe(candles, timeframe).filter((c) => c.timestamp >= firstComplete && inWindow(c));
    },

    async getTickers(wanted?: string[]): Promise<Ticker24h[]> {
      if (wanted && wanted.length > 0 && wanted.length <= FEW_TICKERS) {
        // Kraken takes the pair as BASE/QUOTE (BTC/USD, DOGE/USD) and answers under that key, so no AssetPairs
        // lookup is needed. An unknown pair fails its own request only.
        const lists = await Promise.all(
          wanted.map((pair) =>
            getResult<Record<string, KrakenTicker>>(`/Ticker?pair=${encodeURIComponent(pair)}`)
              .then((result) => mapKrakenTickers(result, new Map(Object.keys(result).map((key) => [key, pair])), Date.now()))
              .catch(() => []),
          ),
        );
        return lists.flat();
      }
      const [markets, result] = await Promise.all([loadMarkets(), getResult<Record<string, KrakenTicker>>('/Ticker')]);
      return mapKrakenTickers(result, markets.symbols, Date.now());
    },

    subscribeOHLCV(pair, timeframe, onCandle, onStatus) {
      const symbol = pair.trim().toUpperCase();
      const { source, minutes } = krakenInterval(timeframe);
      const rolledUp = source !== timeframe;
      const rollup = new CandleRollup(timeframe);
      // A rolled-up candle is only right once REST has filled the bucket; native ones are whole.
      let ready = !rolledUp;
      let stopped = false;
      let lastEmitted = -Infinity;
      let connections = 0;

      const emit = (candle: OHLCV | null) => {
        if (!candle || !ready || stopped || candle.timestamp < lastEmitted) return;
        lastEmitted = candle.timestamp;
        onCandle(candle);
      };
      // The snapshot holds only 10 finer candles (too few for a month of days), so REST fills the bucket.
      const seed = () => {
        const now = Date.now();
        restNameFor(symbol)
          .then((name) => fetchOHLC(name, minutes, bucketStart(now, timeframe)))
          .then(
            ({ candles }) => {
              if (stopped) return;
              ready = true;
              emit(rollup.seed(candles));
            },
            () => {
              if (stopped) return;
              // Carry on with what streams in; the bucket in progress may lack its earlier part.
              ready = true;
              onStatus?.('error');
              emit(rollup.current());
            },
          );
      };

      if (rolledUp) seed();
      const socket = new ReconnectingSocket({
        urls: [WS_URL],
        WebSocket: options.WebSocket,
        onStatus,
        heartbeat: PING,
        onOpen: (send) => {
          if (connections++ > 0) {
            // What streamed before the drop may be stale; the new snapshot (and seed) rebuild the bucket.
            rollup.reset();
            if (rolledUp) {
              ready = false;
              seed();
            }
          }
          send(JSON.stringify({ method: 'subscribe', params: { channel: 'ohlc', symbol: [symbol], interval: minutes } }));
        },
        onMessage: (raw) => {
          const frame = parseKrakenOhlcFrame(raw, symbol, minutes);
          if (!frame) return;
          for (const candle of frame.candles) {
            const rolled = rollup.update(candle);
            if (frame.type === 'update') emit(rolled);
          }
          // A snapshot replays recent history; only the bucket in progress is passed on.
          if (frame.type === 'snapshot') emit(rollup.current());
        },
      });
      return () => {
        stopped = true;
        socket.close();
      };
    },

    subscribeTickers(pairs, onTicker) {
      const symbols = pairs.map((p) => p.trim().toUpperCase());
      if (symbols.length === 0) return () => {};
      const socket = new ReconnectingSocket({
        urls: [WS_URL],
        WebSocket: options.WebSocket,
        heartbeat: PING,
        onOpen: (send) => send(JSON.stringify({ method: 'subscribe', params: { channel: 'ticker', symbol: symbols } })),
        onMessage: (raw) => {
          for (const ticker of parseKrakenTickerFrame(raw)) onTicker(ticker);
        },
      });
      return () => socket.close();
    },
  };
  return adapter;
}

function parseFrame(raw: string): WsFrame | null {
  try {
    const frame = JSON.parse(raw) as unknown;
    return frame && typeof frame === 'object' ? (frame as WsFrame) : null;
  } catch {
    return null;
  }
}

/** Finite number from a venue's string or number field; undefined when absent or unreadable. */
function toNumber(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number.parseFloat(value) : Number.NaN;
  return Number.isFinite(n) ? n : undefined;
}

/** ISO time with any number of fraction digits (Kraken sends up to nanoseconds) → Unix ms. */
function parseTime(iso: string | undefined): number | undefined {
  if (typeof iso !== 'string') return undefined;
  const ms = Date.parse(iso.replace(/(\.\d{3})\d+/, '$1'));
  return Number.isFinite(ms) ? ms : undefined;
}
